/**
 * useOAuthSignIn — starts a social sign-in, preferring a popup window and
 * falling back automatically to a full-page redirect when popups are blocked.
 *
 * WHY two paths in one hook:
 *  - On the website a popup is nicer — it keeps the user on our page and the
 *    SPA never unmounts.
 *  - Inside the UE plugin's WebView2, window.open is typically blocked (returns
 *    null), so we must gracefully fall back to the current full-page redirect.
 *    A single signIn() handles both: try popup → if blocked, navigate inline.
 *
 * Popup flow:
 *   window.open('/api/auth/oauth/<provider>/start', 'gdb-oauth', …)
 *   The backend 302s the popup to the provider, then back to
 *   /auth/oauth/complete#token=<jwt> (success) or /login?oauth_error=<reason>
 *   (failure). Those pages detect they're in a popup and postMessage the
 *   result back here (see oauthMessages.ts), then window.close().
 *   On token → persistOAuthToken (the SAME persistence the inline path uses)
 *   + navigate('/account'). On error → toast.error(oauthErrorMessage(reason)).
 *
 * Inline fallback (popup === null): window.location.href = startUrl — the exact
 * current behavior. The backend redirects the top-level browser to
 * /auth/oauth/complete, which (not being a popup) persists + navigates itself.
 *
 * SECURITY: the message listener ignores any event whose origin !==
 * window.location.origin and any payload that isn't a well-formed
 * 'gdb-oauth-token' / 'gdb-oauth-error' message (parseOAuthMessage).
 *
 * Cleanup: the listener + the popup-closed poll interval are torn down after
 * handling a message, when the popup is closed before completing (silent
 * cancel), AND on unmount — no leaks.
 */

import { useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { authUrl } from '@/lib/api/apiUrl'
import { persistOAuthToken } from './persistOAuthToken'
import { oauthErrorMessage } from './oauthErrors'
import { parseOAuthMessage, OAUTH_TOKEN_MESSAGE } from './oauthMessages'

export type OAuthProviderId = 'google' | 'github'

const POPUP_NAME = 'gdb-oauth'
const POPUP_FEATURES = 'width=500,height=650,menubar=no,toolbar=no'
/** How often to poll popup.closed to detect a cancelled (user-closed) flow. */
const POPUP_POLL_MS = 500

export interface UseOAuthSignInResult {
  signIn: (provider: OAuthProviderId) => void
}

export function useOAuthSignIn(): UseOAuthSignInResult {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Live handles for the in-flight popup attempt so cleanup can always reach
  // them (from the message handler, the poll, and the unmount effect).
  const messageListenerRef = useRef<((event: MessageEvent) => void) | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const cleanup = useCallback(() => {
    if (messageListenerRef.current) {
      window.removeEventListener('message', messageListenerRef.current)
      messageListenerRef.current = null
    }
    if (pollRef.current !== null) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }, [])

  // Tear everything down if the component using the hook unmounts mid-flow.
  useEffect(() => cleanup, [cleanup])

  const signIn = useCallback(
    (provider: OAuthProviderId) => {
      // Build against the CLOUD auth base (authUrl), not a bare path. In
      // cloud-SpaSource mode the origin IS the cloud so this is same-origin;
      // in bundled mode (origin ≠ cloud) authUrl points the OAuth start at the
      // cloud explicitly so the redirect dance still reaches the right host.
      const startUrl = authUrl(`/api/auth/oauth/${provider}/start`)

      // Only one attempt in flight — clear any prior listener/poll first.
      cleanup()

      const popup = window.open(startUrl, POPUP_NAME, POPUP_FEATURES)

      // Blocked (WebView2 / popup blocker): fall back to the full-page redirect
      // — the exact current inline behavior. The backend redirects the
      // top-level browser to /auth/oauth/complete, which persists + navigates.
      if (!popup) {
        console.log('[oauth] popup blocked — falling back to full-page redirect')
        window.location.href = startUrl
        return
      }

      console.log('[oauth] popup opened, awaiting postMessage result')

      const handleMessage = (event: MessageEvent): void => {
        // Same-origin only. Ignore anything from another origin outright.
        if (event.origin !== window.location.origin) return

        const message = parseOAuthMessage(event.data)
        if (!message) return

        cleanup()

        if (message.type === OAUTH_TOKEN_MESSAGE) {
          console.log('[oauth] popup relayed a token, persisting + navigating')
          void (async () => {
            await persistOAuthToken(queryClient, message.token)
            navigate('/account', { replace: true })
          })()
          return
        }

        // Error: friendly toast, no navigation (stay on the current page).
        console.warn('[oauth] popup relayed an error:', message.reason)
        toast.error(oauthErrorMessage(message.reason))
      }

      messageListenerRef.current = handleMessage
      window.addEventListener('message', handleMessage)

      // If the user closes the popup before it completes, treat it as a silent
      // cancel — just clean up, no toast, no navigation.
      pollRef.current = setInterval(() => {
        if (popup.closed) {
          console.log('[oauth] popup closed before completing — cancelled')
          cleanup()
        }
      }, POPUP_POLL_MS)
    },
    [cleanup, navigate, queryClient],
  )

  return { signIn }
}
